import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useTheme } from '../lib/ThemeContext';

/**
 * VisibilityModal
 * Props:
 *   projectId  : string  — 対象案件のID
 *   projectTitle: string — 案件名（表示用）
 *   onClose    : () => void
 */
export default function VisibilityModal({ projectId, projectTitle, onClose }) {
  const { theme } = useTheme();
  const [relations, setRelations] = useState([]); // { id, admin_id, name }
  const [visibleSet, setVisibleSet] = useState(new Set()); // 公開中の admin_freelancer_id
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // 保存中の relation id

  useEffect(() => {
    fetchData();
  }, [projectId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: authData } = await supabase.auth.getUser();
      const user = authData?.user;
      if (!user) { setLoading(false); return; }

      // 自分を管理している管理者一覧
      const { data: rels } = await supabase
        .from('admin_freelancers')
        .select('id, admin_id, profiles!admin_freelancers_admin_id_fkey(name)')
        .eq('freelancer_id', user.id)
        .eq('status', 'active');

      // 現在の公開状態
      const { data: visible } = await supabase
        .from('admin_project_visibility')
        .select('admin_freelancer_id')
        .eq('project_id', projectId);

      const visibleIds = new Set((visible || []).map(v => v.admin_freelancer_id));
      const relList = (rels || []).map(r => ({
        id: r.id,
        admin_id: r.admin_id,
        name: r.profiles?.name || '管理者',
      }));

      setRelations(relList);
      setVisibleSet(visibleIds);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const toggle = async (relationId, isCurrentlyOn) => {
    setSaving(relationId);
    try {
      if (isCurrentlyOn) {
        // 公開OFF → DELETE
        await supabase
          .from('admin_project_visibility')
          .delete()
          .eq('admin_freelancer_id', relationId)
          .eq('project_id', projectId);
        setVisibleSet(prev => {
          const next = new Set(prev);
          next.delete(relationId);
          return next;
        });
      } else {
        // 公開ON → INSERT
        await supabase
          .from('admin_project_visibility')
          .insert({ admin_freelancer_id: relationId, project_id: projectId });
        setVisibleSet(prev => new Set([...prev, relationId]));
      }
    } catch (e) {
      console.error(e);
    }
    setSaving(null);
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        zIndex: 1100, padding: 0,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: theme.bgCard,
          width: '100%', maxWidth: 520,
          borderRadius: '20px 20px 0 0',
          padding: 24, paddingBottom: 40,
        }}
      >
        {/* ヘッダー */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: theme.textPrimary }}>
            👁 公開設定
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: theme.textMuted, fontSize: 20, cursor: 'pointer' }}
          >
            ✕
          </button>
        </div>
        <div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 20 }}>
          📋 {projectTitle}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: theme.textMuted, fontSize: 13 }}>
            読み込み中...
          </div>
        ) : relations.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '24px 0',
            color: theme.textMuted, fontSize: 13,
          }}>
            管理者がいません
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 11, color: theme.textSecondary, marginBottom: 4 }}>
              管理者ごとにON/OFFを切り替えてください（デフォルト：非公開）
            </div>
            {relations.map(rel => {
              const isOn = visibleSet.has(rel.id);
              const isSaving = saving === rel.id;
              return (
                <div
                  key={rel.id}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    background: theme.bgInput,
                    border: `1px solid ${isOn ? '#6366f1' : theme.border}`,
                    borderRadius: 12, padding: '14px 16px',
                    transition: 'border-color 0.2s',
                  }}
                >
                  <span style={{ fontSize: 14, fontWeight: 600, color: theme.textPrimary }}>
                    {rel.name}
                  </span>
                  <button
                    onClick={() => !isSaving && toggle(rel.id, isOn)}
                    disabled={isSaving}
                    style={{
                      position: 'relative',
                      width: 52, height: 28,
                      borderRadius: 14,
                      border: 'none',
                      cursor: isSaving ? 'default' : 'pointer',
                      background: isOn ? '#6366f1' : '#334155',
                      transition: 'background 0.2s',
                      flexShrink: 0,
                      opacity: isSaving ? 0.6 : 1,
                    }}
                  >
                    <span
                      style={{
                        position: 'absolute',
                        top: 4, left: isOn ? 28 : 4,
                        width: 20, height: 20,
                        borderRadius: '50%',
                        background: '#fff',
                        transition: 'left 0.2s',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                      }}
                    />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <button
          onClick={onClose}
          style={{
            marginTop: 24, width: '100%',
            background: theme.bgInput,
            border: `1px solid ${theme.border}`,
            borderRadius: 12, padding: '12px',
            fontSize: 14, fontWeight: 600,
            color: theme.textSecondary, cursor: 'pointer',
          }}
        >
          閉じる
        </button>
      </div>
    </div>
  );
}
